import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ error, errorInfo });

    // If it's a dynamic import failure due to hot server reloads/updates, auto-reload once smoothly
    const errMsg = error?.message || '';
    if (errMsg.includes('Failed to fetch dynamically imported module') || errMsg.includes('Loading chunk')) {
      const storageKey = 'djp_err_boundary_reload_ts';
      const lastReload = sessionStorage.getItem(storageKey);
      const now = Date.now();
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem(storageKey, String(now));
        setTimeout(() => {
          window.location.reload();
        }, 800);
      }
    }
  }

  private handleReload = () => {
    window.location.href = '/';
  };

  private handleClearCacheAndReload = () => {
    try {
      localStorage.removeItem('dojapao_active_tab_v2');
      localStorage.removeItem('dojapao_logged_user_v2');
      localStorage.removeItem('dojapao_is_logged_in_v2');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Could not clear storage:', e);
    }
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-rose-50 border border-rose-100 rounded-full flex items-center justify-center mx-auto text-[#E63946]">
              <AlertTriangle className="w-8 h-8" />
            </div>

            <div className="space-y-2">
              <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-bold tracking-wider uppercase">
                Do Japão até a Sua Casa
              </span>
              <h1 className="text-xl font-black text-[#1D3557]">
                Ops! Ocorreu uma instabilidade
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed">
                Detectamos uma falha temporária no carregamento da aplicação. Você pode recarregar a página com segurança abaixo.
              </p>
            </div>

            {this.state.error && (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-left overflow-auto max-h-28">
                <p className="text-xs font-mono text-slate-700 break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="space-y-3 pt-2">
              <button
                onClick={this.handleReload}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1D3557] hover:bg-[#152740] text-white font-bold rounded-xl shadow-md transition-all cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                Recarregar Página
              </button>

              <button
                onClick={this.handleClearCacheAndReload}
                className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-all cursor-pointer"
              >
                <Trash2 className="w-4 h-4 text-slate-500" />
                Limpar Cache Local & Voltar ao Início
              </button>
            </div>

            <div className="text-xs text-slate-400">
              Depósito Saitama-ken, Kawagoe-shi • Do Japão Forwarding
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
